import {
	UserProfileViewModel,
	getUserProfileBackgroundImageUrl,
	getUserProfileImageUrl
} from "client/Api/UserProfile/service";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { PersonalInformationPage } from "client/Features/PersonalInformationPage/PersonalInformationPage";
import { IFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	isNotNil,
	isNotNilOrEmpty,
	isNotNilOrWhiteSpace,
	getDataUrl
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { TimeZone } from "client/DateAndTime/timezones";
import { GeneralInformationSection } from "client/Features/AddressBook/Details/Sections/GeneralInformationSection";
import { MultilineTextSection } from "client/Features/AddressBook/Details/Sections/MultilineTextSection";
import { WorkingDaysSection } from "client/Features/AddressBook/Details/Sections/WorkingDaysSection";
import { TagsSection } from "client/Features/AddressBook/Details/Sections/TagsSection";
import { convertToDateOnly, parseDtoDateOnlyIgnoringOffset } from "client/DateAndTime/dateUtils";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { getIdentifiesAs } from "./UserProfileForm";
import { FormData } from "./UserProfilePage";
import { Base64FileUriUploadWithMetadataModel, fileMetadataSymbol } from "client/Misc/commonTypes";

export type UserProfileDetailsProps = {
	imageFormHandler: IFormHandler<FormData>;
	viewModel: UserProfileViewModel;
	currentAvatarId: string | null;
	currentBackgroundImageId: string | null;
	timezone: TimeZone;
};

export const UserProfileDetails = (props: UserProfileDetailsProps): ReactElement => {
	const {
		imageFormHandler,
		viewModel,
		currentAvatarId,
		currentBackgroundImageId,
		timezone
	} = props;

	const {
		model,
		avatarSelectorOptions,
		backgroundImageSelectorOptions,
		editRestrictions
	} = viewModel;

	const {
		computedDisplayName
	} = useMemo(
		() => {
			return getComputedDisplayName({
				firstName: model.firstName,
				lastName: model.lastName
			});
		},
		[
			model.firstName,
			model.lastName
		]
	);

	const avatarUrl = useMemo(
		(): string | undefined => {
			const selectedAvatar: OptionalModel<Base64FileUriUploadModel> = imageFormHandler.for("avatar")
				.value;

			if (selectedAvatar.isSet) {
				return selectedAvatar.value
					.uri;
			}

			if (isNotNil(currentAvatarId)) {
				return getUserProfileImageUrl(currentAvatarId);
			}

			return undefined;
		},
		[currentAvatarId, imageFormHandler]
	);

	const backgroundImageUrl = useMemo(
		(): string | undefined => {
			const selectedImage: OptionalModel<Base64FileUriUploadModel | null> = imageFormHandler.for("backgroundImage")
				.value;

			if (selectedImage.isSet) {
				return selectedImage.value
					?.uri;
			}

			if (isNotNil(currentBackgroundImageId)) {
				return getUserProfileBackgroundImageUrl(currentBackgroundImageId);
			}

			return undefined;
		},
		[
			currentBackgroundImageId,
			imageFormHandler
		]
	);

	const info = useMemo(
		(): string => {
			const sections: string[] = [
				model.jobTitle,
				model.department,
				model.officeLocation
			]
				.filter(isNotNilOrEmpty);

			return sections.join(" | ");
		},
		[
			model.department,
			model.jobTitle,
			model.officeLocation
		]
	);

	const fileChangeHandler = useCallback(
		async (
			file: File | null,
			attachable: IValueInputAttachable<OptionalModel<Base64FileUriUploadWithMetadataModel | null>>
		): Promise<void> => {
			let newValue: Base64FileUriUploadWithMetadataModel | null = null;

			if (file) {
				const fileUrl: string = await getDataUrl(file);

				newValue = {
					uri: fileUrl,
					filename: file.name,
					[fileMetadataSymbol]: {
						size: file.size,
						mimeType: file.type
					}
				};
			}

			const {
				reducer,
				validation
			} = attachable;

			reducer.setValue({
				isSet: true,
				value: newValue
			});

			validation.triggerInputFinished();
		},
		[]
	);

	const avatarChangeHandler = useCallback(
		async (file: File): Promise<void> => {
			await fileChangeHandler(file, imageFormHandler.for("avatar"));
		},
		[fileChangeHandler, imageFormHandler]
	);

	const backgroundImageChangeHandler = useCallback(
		async (file: File | null): Promise<void> => {
			await fileChangeHandler(file, imageFormHandler.for("backgroundImage"));
		},
		[fileChangeHandler, imageFormHandler]
	);

	const canEditAvatar: boolean = !editRestrictions.profileImage
		.isDisabled;

	const birthday = useMemo(
		(): DateOnly | null => {
			if (!model.upcomingBirthday) {
				return null;
			}

			if (typeof model.upcomingBirthday === "string") {
				return parseDtoDateOnlyIgnoringOffset(model.upcomingBirthday);
			}

			return convertToDateOnly(model.upcomingBirthday, timezone);
		},
		[timezone, model.upcomingBirthday]
	);

	const identifiesAs = useMemo(
		(): string | null => getIdentifiesAs(model.pronouns),
		[model.pronouns]
	);

	return (
		<PersonalInformationPage trackingId="user-profile">
			<PersonalInformationPage.Banner
				{...(canEditAvatar
					? {
						onChangeAvatar: avatarChangeHandler,
						avatarContentTypes: avatarSelectorOptions.supportedImageTypes
					}
					: {
						onChangeAvatar: undefined
					}
				)}
				fullName={computedDisplayName}
				identifiesAs={identifiesAs}
				avatarUrl={avatarUrl}
				avatarPlaceholder={{ kind: "user-icon" }}
				backgroundImageUrl={backgroundImageUrl}
				info={info}
				onChangeBackgroundImage={backgroundImageChangeHandler}
				backgroundImageContentTypes={backgroundImageSelectorOptions.supportedImageTypes}
				trackingId="banner"
			/>
			<GeneralInformationSection
				birthday={birthday}
				emailAddresses={[
					model.emailAddress,
					model.secondaryEmailAddress
				]
					.filter(isNotNilOrWhiteSpace)
				}
				phoneNumbers={{
					mobilePhone: model.phoneNumber
				}}
				timezone={timezone}
				emptyBehavior="placeholder"
				customString1={model.customString1}
				customString2={model.customString2}
				customString3={model.customString3}
				customString4={model.customString4}
				customString5={model.customString5}
			/>
			<MultilineTextSection
				label={localize("_AboutMe")}
				trackingId="about-me"
				text={model.aboutMe}
				emptyBehavior="placeholder"
			/>
			<WorkingDaysSection
				workingDays={model.workingDays}
				emptyBehavior="placeholder"
			/>
			<TagsSection
				title={localize("_Projects")}
				trackingId="projects"
				tags={model.projects}
				emptyBehavior="placeholder"
			/>
			<TagsSection
				title={localize("_SkillsAndExpertise")}
				trackingId="skills-and-expertise"
				tags={model.skills}
				emptyBehavior="placeholder"
			/>
			<TagsSection
				title={localize("_SchoolsAndEducation")}
				trackingId="schools-and-education"
				tags={model.education}
				emptyBehavior="placeholder"
			/>
			<TagsSection
				title={localize("_InterestsAndHobbies")}
				trackingId="interests-and-hobbies"
				tags={model.hobbies}
				emptyBehavior="placeholder"
			/>
		</PersonalInformationPage>
	);
};
UserProfileDetails.displayName = "UserProfileDetails";